import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'motion/react';
import { Flame, Hash, Eye, Star, Video } from 'lucide-react';
import { UserAvatar, PageHeader } from '@/components/ui';
import { http } from '@/network/http';

interface TrendingVideo {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  viewCount: number;
  creatorName: string;
}

interface TrendingHashtag {
  id: string;
  name: string;
  videoCount: number;
}

interface FeaturedCreator {
  id: string;
  displayName: string;
  handle: string;
  avatarUrl: string | null;
  followerCount: number;
}

export function ExplorePage() {
  const { t } = useTranslation();

  const { data: trendingVideos, isLoading: loadingVideos } = useQuery<TrendingVideo[]>({
    queryKey: ['trending', 'videos'],
    queryFn: async () => {
      const res = await http.get('/trending/videos');
      return res.data;
    },
  });

  const { data: trendingHashtags, isLoading: loadingHashtags } = useQuery<TrendingHashtag[]>({
    queryKey: ['trending', 'hashtags'],
    queryFn: async () => {
      const res = await http.get('/trending/hashtags');
      return res.data;
    },
  });

  const { data: featuredCreators, isLoading: loadingCreators } = useQuery<FeaturedCreator[]>({
    queryKey: ['trending', 'creators'],
    queryFn: async () => {
      const res = await http.get('/trending/creators');
      return res.data;
    },
  });

  const isLoading = loadingVideos || loadingHashtags || loadingCreators;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-8 px-4 sm:px-6 md:px-8"
    >
      <PageHeader
        icon={<Flame className="h-6 w-6 text-orange-500" />}
        title={t('explore.title')}
        subtitle={t('explore.subtitle')}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <p className="text-slate-500">{t('common.loading')}</p>
        </div>
      ) : (
        <>
          {/* Trending Videos */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Flame className="h-5 w-5 text-orange-500" />
              <h2 className="text-base font-semibold text-slate-800">
                {t('explore.trendingVideos')}
              </h2>
            </div>

            {(trendingVideos?.length ?? 0) === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Video className="h-10 w-10 text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">{t('explore.noVideos')}</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {trendingVideos!.map((video) => (
                  <motion.div
                    key={video.id}
                    whileHover={{ y: -2 }}
                    className="rounded-xl overflow-hidden transition-shadow hover:shadow-lg"
                    style={{
                      background: 'rgba(255,255,255,0.7)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(148,163,184,0.2)',
                    }}
                  >
                    {video.thumbnailUrl ? (
                      <img
                        src={video.thumbnailUrl}
                        alt={video.title}
                        className="aspect-video w-full object-cover"
                      />
                    ) : (
                      <div className="aspect-video w-full bg-slate-100 flex items-center justify-center">
                        <Video className="h-8 w-8 text-slate-300" />
                      </div>
                    )}
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-slate-900 truncate">
                        {video.title}
                      </h3>
                      <p className="text-xs text-slate-500 mt-1">{video.creatorName}</p>
                      <span className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                        <Eye className="h-3.5 w-3.5" />
                        {video.viewCount.toLocaleString()}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </section>

          {/* Trending Hashtags */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Hash className="h-5 w-5 text-indigo-500" />
              <h2 className="text-base font-semibold text-slate-800">
                {t('explore.trendingHashtags')}
              </h2>
            </div>

            {(trendingHashtags?.length ?? 0) === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Hash className="h-10 w-10 text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">{t('explore.noHashtags')}</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {trendingHashtags!.map((hashtag) => (
                  <motion.div
                    key={hashtag.id}
                    whileHover={{ scale: 1.04 }}
                    className="flex items-center gap-2 rounded-full px-4 py-2 text-sm cursor-pointer transition-colors hover:bg-indigo-50"
                    style={{
                      background: 'rgba(255,255,255,0.7)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(148,163,184,0.2)',
                    }}
                  >
                    <Hash className="h-4 w-4 text-indigo-500" />
                    <span className="font-medium text-slate-800">{hashtag.name}</span>
                    <span className="text-xs text-slate-400">
                      {hashtag.videoCount.toLocaleString()}
                    </span>
                  </motion.div>
                ))}
              </div>
            )}
          </section>

          {/* Featured Creators */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Star className="h-5 w-5 text-amber-500" />
              <h2 className="text-base font-semibold text-slate-800">
                {t('explore.featuredCreators')}
              </h2>
            </div>

            {(featuredCreators?.length ?? 0) === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Star className="h-10 w-10 text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">{t('explore.noCreators')}</p>
              </div>
            ) : (
              <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 hide-scrollbar">
                {featuredCreators!.map((creator) => (
                  <motion.div
                    key={creator.id}
                    whileHover={{ y: -2 }}
                    className="flex-shrink-0 w-40 rounded-xl p-4 text-center transition-shadow hover:shadow-lg"
                    style={{
                      background: 'rgba(255,255,255,0.7)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(148,163,184,0.2)',
                    }}
                  >
                    <div className="flex justify-center mb-3">
                      <UserAvatar
                        src={creator.avatarUrl}
                        name={creator.displayName}
                        size="lg"
                      />
                    </div>
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {creator.displayName}
                    </p>
                    <p className="text-xs text-slate-500 truncate">@{creator.handle}</p>
                    <p className="text-xs text-slate-400 mt-1.5">
                      {creator.followerCount.toLocaleString()} {t('explore.followers')}
                    </p>
                  </motion.div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </motion.div>
  );
}
